import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { SiteFooter } from "@/components/SiteFooter";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";

import { BUSINESS_FAQ_CATEGORIES } from "@/content/business-corpus/faq-defs";

import { getActiveProvince } from "@/provinces/_registry";

export const Route = createFileRoute("/business/faq/")({
  loader: async (): Promise<{ BUSINESS_FAQ_TH: { q: string; a: string }[]; MASTER_KEYWORDS_ALL: string[]; MASTER_QUESTIONS_TH: string[] }> => {
    const [m0, m1] = await Promise.all([
      import("@/content/business-corpus/categories"),
      import("@/content/master-corpus"),
    ]);
    return { BUSINESS_FAQ_TH: m0.BUSINESS_FAQ_TH, MASTER_KEYWORDS_ALL: m1.MASTER_KEYWORDS_ALL, MASTER_QUESTIONS_TH: m1.MASTER_QUESTIONS_TH };
  },
  component: BusinessFaqHub,
  head: ({ loaderData }) => {
    const { BUSINESS_FAQ_TH = [], MASTER_KEYWORDS_ALL = [], MASTER_QUESTIONS_TH = [] } = loaderData ?? {};

    const { config } = getActiveProvince();
    const site = getActiveSiteUrl();
    return {
      ...pageHeadForProvince({
        title: `ล่าม BOI บัญชี-ภาษี Expat VIP — FAQ ธุรกิจ 2026 | ${config.nameTh}`,
        description:
          "คำถามที่พบบ่อยสำหรับนักลงทุนและชาวต่างชาติ — จองล่ามราชการ/ศาล เอกสาร BOI ควบรวมกิจการ งบการเงิน ภาษี 180 วัน และแพ็กเกจ Expat VIP ตอบโดยทีมทนาย",
        path: "/business/faq",
        type: "article",
        alternates: [{ lang: "en", path: "/en/business/faq" }],
        keywords: MASTER_KEYWORDS_ALL.slice(0, 80),
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "FAQ ธุรกิจและนักลงทุน", url: `${site}/business/faq` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(BUSINESS_FAQ_TH)) },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", "h2", ".faq-answer"] },
            keywords: MASTER_KEYWORDS_ALL.slice(0, 100).join(", "),
          }),
        },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/business/faq")) },
      ],
    };
  },
});

function BusinessFaqHub() {
  const { BUSINESS_FAQ_TH, MASTER_KEYWORDS_ALL, MASTER_QUESTIONS_TH } = Route.useLoaderData() as { BUSINESS_FAQ_TH: { q: string; a: string }[]; MASTER_KEYWORDS_ALL: string[]; MASTER_QUESTIONS_TH: string[] };
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/" className="hover:underline">หน้าแรก</a> <span>/</span> <span>FAQ ธุรกิจและนักลงทุน</span>
        </nav>
        <h1 className="text-3xl md:text-4xl font-bold mb-3">
          ล่าม · BOI · บัญชี-ภาษี · Expat VIP — FAQ {BUSINESS_FAQ_TH.length} ข้อ (2026)
        </h1>
        <p className="text-muted-foreground mb-8">
          คำตอบฉบับทนายสำหรับนักลงทุนต่างชาติและผู้บริหารที่ย้ายมาประเทศไทย — การจัดล่ามในงานราชการและชั้นศาล
          เอกสารขอบัตรส่งเสริม BOI การซื้อขายหุ้นและควบรวมกิจการ งบการเงินและหนังสือรับรองภาษี
          รวมถึงแพ็กเกจดูแลแบบครบวงจร
        </p>

        <div className="mb-6 rounded-lg border bg-muted/30 p-4 text-sm">
          <strong>English:</strong>{" "}
          <a href="/en/business/faq" className="text-primary underline">
            Business, BOI &amp; expat FAQ →
          </a>
        </div>

        <section aria-label="หมวดคำถามธุรกิจ" className="mb-8">
          <h2 className="text-lg font-semibold mb-3">หมวดคำถาม</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {BUSINESS_FAQ_CATEGORIES.map((c) => (
              <a
                key={c.slug}
                href={`/business/faq/${c.slug}`}
                className="rounded-lg border bg-card p-3 text-sm transition-colors hover:border-primary hover:bg-primary/5"
              >
                <div className="font-semibold mb-1">{c.titleTh} →</div>
                <div className="text-xs text-muted-foreground line-clamp-2">{c.descTh}</div>
              </a>
            ))}
          </div>
        </section>

        <div className="space-y-4">
          {BUSINESS_FAQ_TH.map((item, i) => (
            <details key={i} className="rounded-lg border bg-card p-4 open:shadow-sm" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">
                <span className="text-muted-foreground mr-2">Q{i + 1}.</span>{item.q}
              </summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>

        <section className="mt-12 rounded-lg border p-6 bg-primary/5">
          <h2 className="text-xl font-bold mb-2">ให้เราดูแลงานเอกสารธุรกิจแบบครบวงจร</h2>
          <p className="text-sm text-muted-foreground mb-4">
            ตรวจเอกสารต้นทาง → แปลรับรอง → ทนายรับรอง/นิติกรณ์ MFA → Apostille 2026 → ยื่น DBD/BOI/ตม. พร้อมล่ามประกบ
          </p>
          <a href="/contact" className="inline-block rounded-md bg-primary px-5 py-2 text-primary-foreground font-medium">
            ปรึกษาทีมองค์กร →
          </a>
        </section>

        <section className="mt-12">
          <h2 className="text-xl font-bold mb-3">คำถามที่ผู้ใช้ค้นหาบ่อย ({MASTER_QUESTIONS_TH.length} คำถาม)</h2>
          <ul className="grid gap-1 text-sm text-muted-foreground md:grid-cols-2">
            {MASTER_QUESTIONS_TH.slice(0, 150).map((q, i) => (
              <li key={i}>• {q}</li>
            ))}
          </ul>
        </section>

        <section className="mt-12">
          <h2 className="text-xl font-bold mb-3">หัวข้อยอดฮิต ({MASTER_KEYWORDS_ALL.length} คำค้น)</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {MASTER_KEYWORDS_ALL.slice(0, 300).map((kw, i) => (
              <span key={i} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground">{kw}</span>
            ))}
          </div>
        </section>
      
        <FaqSourceNote topic="business" locale="th" />
      </main>
      <SiteFooter />
    </>
  );
}